/** @format */

/**
 * Trace 详情页的 Span 树形表格：按父子关系展开/收起，展示耗时条、资源与 GenAI Token 摘要。
 * 支持 Kind/关键字/仅错误 Span 筛选（命中行在主列高亮）。
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'
import { _$, Icon, Table, TableColumn, TableRowKey, Tag, Tooltip } from '@king-design/react'
import { secondsFormat } from 'src/utils'
import type { TraceAnalysisDetailResponse, TraceAnalysisDetailSpan } from '../typescript'
import { SessionDetailSpanStatusCode } from '../../SessionAnalysis/SessionDetail/typescript'
import { formatCompactK } from 'src/components/FormatCompactK'
import variable from 'src/static/styles/var'
import {
  buildLangGraphTableTreeData,
  buildSpanChildrenMap,
  collectLangGraphTableExpandableIds,
  isLangGraphTrace
} from './langgraphSpanTree'
import {
  buildSpanTree,
  collectExpandableSpanIds,
  getMatchedSpanIds,
  LANGGRAPH_ROOT_KEY,
  toTableTreeData
} from './traceSpanShared'
import type { SpanTableRow } from './traceSpanShared'

export interface TraceSpanTreeProps {
  className?: string
  detail: TraceAnalysisDetailResponse | null | undefined
  selectedTags?: string[]
  searchValue?: string
  showErrorSpans?: boolean
  /** 当前选中的 SpanId（由父组件管理） */
  selectedSpanId?: string | null
  /** 点击 Span 行时触发 */
  onSelectSpan?: (span: TraceAnalysisDetailSpan) => void
}

interface TokenInfo {
  input: string | null
  output: string | null
  total: string | null
  cache: string | null
}

/** 从 Span 字段或 Attributes 读取 Token（兼容网关字段与 OTLP 语义）。 */
function getTokenInfo(span: TraceAnalysisDetailSpan): TokenInfo | null {
  return {
    input: span.InputTokens ? String(span.InputTokens) : null,
    output: span.OutputTokens ? String(span.OutputTokens) : null,
    total: span.TotalTokens ? String(span.TotalTokens) : null,
    cache: span.CacheReadTokens ? String(span.CacheReadTokens) : null
  }
}

function tokenHintText(info: TokenInfo): string | null {
  if (info.input != null && info.output != null && info.total != null) {
    return `${info.input} -> ${info.output} (Σ${info.total})`
  }
  if (info.total != null) {
    return `Σ${info.total}`
  }
  return null
}

/**
 * 耗时条颜色：与 Trace 列表 Tag 规则一致（毫秒）
 * durationMs <=10s：绿色
 * 10s < durationMs <= 30s：黄色
 * durationMs > 30s：红色
 */
export function getDurationBarTone(durationMs: number): string {
  if (durationMs <= 10 * 1000) {
    return variable.successColor
  }
  if (durationMs <= 30 * 1000) {
    return variable.warningColor
  }
  return variable.errorColor
}

/**
 * 列表左侧状态圆标（与 `spanStatusVisual` 对应）。
 * ERROR：红色
 * UNSET：黄色
 * OK：绿色
 */
const StatusIcon = ({ status }: { status: SessionDetailSpanStatusCode }) => {
  if (status === 'ERROR') {
    return <Icon className="ion-close-circled" color="danger" size={16} />
  }

  if (status === 'UNSET') {
    return <Icon className="ion-information-circled" color="warning" size={16} />
  }

  return <Icon className="ion-checkmark-circled" color="success" size={16} />
}

/** 各列相对宽度（原 300 / 180 / 100 / 150 px 比例） */
const TRACE_SPAN_COL_WIDTHS = {
  main: 300,
  duration: 180,
  start: 100,
  service: 100
} as const
const TRACE_SPAN_COL_TOTAL =
  TRACE_SPAN_COL_WIDTHS.main +
  TRACE_SPAN_COL_WIDTHS.duration +
  TRACE_SPAN_COL_WIDTHS.start +
  TRACE_SPAN_COL_WIDTHS.service

function toColPercent(width: number): string {
  return `${((width / TRACE_SPAN_COL_TOTAL) * 100).toFixed(2)}%`
}

/** Token 明细内容（用于 Tooltip slotContent）。 */
function TokenTooltipContent({ info }: { info: TokenInfo }) {
  return (
    <div className="trace-span-token-tooltip" role="tooltip">
      <div className="trace-span-token-tooltip-title">{_$('Token 消耗')}</div>
      <div className="trace-span-token-tooltip-row">
        <span>{_$('Total Tokens')}</span>
        <span>{info.total ? formatCompactK(Number(info.total)) : '-'}</span>
      </div>
      <div className="trace-span-token-tooltip-row">
        <span>{_$('Input 缓存 Token')}</span>
        <span>{info.cache ? formatCompactK(Number(info.cache)) : '-'}</span>
      </div>
      <div className="trace-span-token-tooltip-row">
        <span>Input Token</span>
        <span>{info.input ? formatCompactK(Number(info.input)) : '-'}</span>
      </div>
      <div className="trace-span-token-tooltip-row">
        <span>Output Token</span>
        <span>{info.output ? formatCompactK(Number(info.output)) : '-'}</span>
      </div>
    </div>
  )
}

const TraceSpanTree: React.FC<TraceSpanTreeProps> = ({
  className,
  detail,
  selectedTags = [],
  searchValue = '',
  showErrorSpans = false,
  selectedSpanId = null,
  onSelectSpan
}) => {
  const allSpans = detail?.Spans ?? []

  const matchedSpanIds = useMemo(
    () => getMatchedSpanIds(allSpans, { searchValue, selectedTags, showErrorSpans }),
    [allSpans, searchValue, selectedTags, showErrorSpans]
  )

  /** Trace 时间轴：起点与总时长，用于耗时条 left / width 百分比 */
  const traceTimeline = useMemo(() => {
    let startMs = 0
    if (detail?.StartTime) {
      startMs = dayjs(detail.StartTime).valueOf()
    } else if (allSpans.length) {
      startMs = Math.min(...allSpans.map(span => dayjs(span.StartTime).valueOf()))
    }
    let totalMs = detail?.DurationMs ?? 0
    if (totalMs <= 0 && allSpans.length) {
      const endMs = Math.max(
        ...allSpans.map(span => dayjs(span.StartTime).valueOf() + span.DurationMs)
      )
      totalMs = Math.max(endMs - startMs, 1)
    }
    return { startMs, totalMs: totalMs > 0 ? totalMs : 1 }
  }, [detail?.StartTime, detail?.DurationMs, allSpans])

  const langGraphMode = useMemo(() => isLangGraphTrace(allSpans), [allSpans])

  const treeRoots = useMemo(() => {
    const map = buildSpanChildrenMap(allSpans)
    return buildSpanTree(map, LANGGRAPH_ROOT_KEY, langGraphMode)
  }, [allSpans, langGraphMode])

  const tableData = useMemo(() => {
    if (langGraphMode) {
      return buildLangGraphTableTreeData(allSpans)
    }
    return toTableTreeData(treeRoots)
  }, [allSpans, langGraphMode, treeRoots])
  /** `null` 表示尚未手动改写，使用默认全展开。 */
  const [spreadKeys, setSpreadKeys] = useState<TableRowKey[] | null>(null)

  useEffect(() => {
    setSpreadKeys(null)
  }, [detail?.TraceId])

  const effectiveSpreadKeys = useMemo(() => {
    if (spreadKeys !== null) {
      return spreadKeys
    }
    if (langGraphMode) {
      return collectLangGraphTableExpandableIds(tableData)
    }
    return collectExpandableSpanIds(treeRoots)
  }, [spreadKeys, treeRoots, langGraphMode, tableData])

  const handleChangeSpreadKeys = useCallback((keys: TableRowKey[]) => {
    setSpreadKeys(keys)
  }, [])

  const handleRowClassName = useCallback(
    (row: SpanTableRow) => {
      const classNames: string[] = []
      if (selectedSpanId === row.SpanId) {
        classNames.push('is-selected')
      }
      if (matchedSpanIds.has(row.SpanId)) {
        classNames.push('is-filtered-match')
      }
      return classNames.length ? classNames.join(' ') : undefined
    },
    [matchedSpanIds, selectedSpanId]
  )

  const handleClickRow = useCallback(
    (row: SpanTableRow) => {
      onSelectSpan?.(row)
    },
    [onSelectSpan]
  )

  const renderSpanCell = useCallback(([span]: [SpanTableRow, number]) => {
    const tokenInfo = getTokenInfo(span)
    const tokenText = tokenInfo ? tokenHintText(tokenInfo) : null
    const kindLabel = span.Kind ? span.Kind.toLowerCase() : ''

    return (
      <div className="trace-span-main-cell">
        <StatusIcon status={span.StatusCode as SessionDetailSpanStatusCode} />
        <span className="trace-span-tree-title" title={span.Name}>
          {span.Name || span.SpanId}
        </span>
        {kindLabel ? <span className="trace-span-kind-badge">{kindLabel}</span> : null}
        {tokenText && tokenInfo ? (
          <span className="trace-span-token-wrap">
            <span className="trace-span-token-text">{tokenText}</span>
            <Tooltip
              theme="light"
              position="top"
              container=".trace-span-tree-table"
              hoverable
              slotContent={<TokenTooltipContent info={tokenInfo} />}
            >
              <span
                className="trace-span-token-trigger"
                tabIndex={0}
                onClick={event => event.stopPropagation()}
              >
                <svg viewBox="0 0 16 16" aria-hidden>
                  <path d="M2 4h12v2H2V4zm0 4h8v2H2V8zm0 4h10v2H2v-2z" fill="currentColor" />
                </svg>
              </span>
            </Tooltip>
          </span>
        ) : null}
      </div>
    )
  }, [])

  const renderDurationCell = useCallback(
    ([span]: [SpanTableRow, number]) => {
      const spanStartMs = dayjs(span.StartTime).valueOf()
      const offsetMs = Math.max(0, spanStartMs - traceTimeline.startMs)
      const leftPct = Math.min(100, Math.max(0, (offsetMs / traceTimeline.totalMs) * 100))
      const rawWidthPct = (span.DurationMs / traceTimeline.totalMs) * 100
      const widthPct = Math.max(0.5, Math.min(100 - leftPct, rawWidthPct))
      const durationTone = getDurationBarTone(span.DurationMs)
      return (
        <div className="trace-span-duration-cell">
          <span className="dur-text">{secondsFormat(span.DurationMs)}</span>
          <div className="dur-track">
            <div
              className="dur-fill"
              style={{ left: `${leftPct}%`, width: `${widthPct}%`, backgroundColor: durationTone }}
            />
          </div>
        </div>
      )
    },
    [traceTimeline.startMs, traceTimeline.totalMs]
  )

  const renderServiceCell = useCallback(
    ([span]: [SpanTableRow, number]) => {
      const serviceName = span.Resource?.['service.name'] ?? detail?.ServiceName ?? '-'
      return <Tag color={span.BgColor}>{serviceName || '-'}</Tag>
    },
    [detail?.ServiceName]
  )

  /** 表头耗时列右侧刻度：整条 Trace 总时长。 */
  const traceDurLabel = secondsFormat(traceTimeline.totalMs)

  if (!tableData.length) {
    return (
      <div className={className}>
        <div className="trace-span-tree-empty">{_$('暂无 Span')}</div>
      </div>
    )
  }

  return (
    <div className={className}>
      <div className="trace-span-tree-table">
        <Table
          data={tableData}
          rowKey={row => row.SpanId}
          checkType="none"
          animation={false}
          rowExpandable={false}
          spreadKeys={effectiveSpreadKeys}
          resizable
          onChangeSpreadKeys={handleChangeSpreadKeys}
          rowClassName={handleRowClassName}
          onClickRow={handleClickRow}
          slotEmpty={<div className="trace-span-tree-empty">{_$('暂无 Span')}</div>}
        >
          <TableColumn
            key="span"
            title="Span"
            width={`${toColPercent(TRACE_SPAN_COL_WIDTHS.main)}`}
            fixed="left"
            slotTemplate={renderSpanCell}
          />
          <TableColumn
            key="duration"
            width={`${toColPercent(TRACE_SPAN_COL_WIDTHS.duration)}`}
            slotTitle={
              <div className="trace-span-dur-head">
                <span>{_$('耗时')}</span>
                <span className="trace-span-dur-scale">{traceDurLabel}</span>
              </div>
            }
            slotTemplate={renderDurationCell}
          />
          <TableColumn
            key="startTime"
            title={_$('起始时间')}
            width={`${toColPercent(TRACE_SPAN_COL_WIDTHS.start)}`}
            slotTemplate={([span]) => dayjs(span.StartTime).format('YYYY-MM-DD HH:mm:ss')}
          />
          <TableColumn
            key="service"
            title={_$('服务名')}
            width={`${toColPercent(TRACE_SPAN_COL_WIDTHS.service)}`}
            slotTemplate={renderServiceCell}
          />
        </Table>
      </div>
    </div>
  )
}

/** Table 树形列表样式：选中/筛选高亮、耗时条、Token tooltip。 */
export default styled(TraceSpanTree)`
  width: 100%;
  min-width: 0;

  .trace-span-tree-empty {
    padding: 32px;
    text-align: center;
    color: rgba(0, 0, 0, 0.45);
    font-size: 13px;
  }

  .trace-span-tree-table {
    width: 100%;
    max-width: 100%;
    overflow: auto;
    border-radius: 4px;

    .k-table {
      font-size: 12px;
      max-height: calc(100vh - 400px);
    }

    .k-table table {
      table-layout: fixed;
      width: 100%;
    }

    .k-table th {
      height: 36px;
      padding: 4px 12px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.55);
      background: #fafafa;
    }

    .k-table td {
      padding: 8px 12px;
      vertical-align: middle;
      /* background: none; */
    }

    .k-table tbody {
      tr {
        cursor: pointer;
        transition: background-color 0.15s ease;

        &:hover {
          background: rgba(0, 0, 0, 0.02);
        }

        &.is-selected {
          background: rgb(230, 247, 255);
          box-shadow: inset 3px 0 0 rgb(0, 145, 234);
          & td {
            background: rgb(230, 247, 255);
          }
          &:hover {
            background: rgb(230, 247, 255);
          }
        }

        &.is-filtered-match .trace-span-main-cell {
          background-color: rgb(255, 251, 0, 0.6);
        }
      }
    }

    .trace-span-dur-head {
      display: flex;
      justify-content: space-between;
      gap: 8px;
      padding-right: 4px;

      .trace-span-dur-scale {
        color: rgba(0, 0, 0, 0.45);
        font-weight: 400;
      }
    }
  }

  .trace-span-main-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    max-width: calc(100% - 20px);
    padding: 2px 0;

    .trace-span-tree-title {
      flex-shrink: 1;
      min-width: 0;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.85);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .trace-span-kind-badge {
      flex-shrink: 0;
      padding: 0 6px;
      line-height: 18px;
      border-radius: 4px;
      background: #e6f4ff;
      color: #1677ff;
      font-size: 11px;
    }

    .trace-span-token-wrap {
      flex-shrink: 0;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      color: rgba(0, 0, 0, 0.55);
      font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;

      .trace-span-token-trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        color: rgba(0, 0, 0, 0.45);
        cursor: default;

        svg {
          width: 12px;
          height: 12px;
        }
      }
    }
  }

  .trace-span-token-tooltip {
    min-width: 180px;
    font-family: inherit;

    .trace-span-token-tooltip-title {
      margin-bottom: 8px;
      font-weight: 500;
      color: rgba(0, 0, 0, 0.85);
    }

    .trace-span-token-tooltip-row {
      display: flex;
      justify-content: space-between;
      gap: 16px;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.65);
    }
  }

  .trace-span-duration-cell {
    min-width: 0;

    .dur-text {
      display: block;
      margin-bottom: 4px;
      font-variant-numeric: tabular-nums;
    }

    .dur-track {
      position: relative;
      height: 8px;
      border-radius: 4px;
      background: rgba(0, 0, 0, 0.04);
      overflow: hidden;

      .dur-fill {
        position: absolute;
        top: 0;
        height: 100%;
        border-radius: 4px;
        min-width: 2px;
        transition: left 0.2s ease, width 0.2s ease;
      }
    }
  }

  .trace-span-tree-table .k-tag {
    height: auto;
    line-height: 16px;
  }
`
